import { queryKeys } from "@/lib/utils/query-keys"
import { sanitize } from "@/lib/utils/sanitize"
import ProductDetails from "@/pages/product"
import type { HttpTypes } from "@medusajs/types"
import { createFileRoute, notFound } from "@tanstack/react-router"
import {
  PREORDER_ENABLED,
  SITE_NAME,
} from "@/lib/constants/site"
import { SITE_URL } from "@/lib/constants/site-url"
import {
  filterVisibleProducts,
  isExpiredCampaignProductHandle,
  isStoreCatalogExcludedHandle,
} from "@/lib/utils/catalog-visibility"
import { isVariantInStock } from "@/lib/utils/product"
import { jsonLd } from "@/lib/utils/json-ld"

export const Route = createFileRoute("/$countryCode/products/$handle")({
  loader: async ({ params, context }) => {
    const { countryCode, handle } = params

    // Pre-order is hidden site-wide (PREORDER_ENABLED) — 404 every product
    // detail page so nothing in the shop stays directly reachable/orderable.
    if (!PREORDER_ENABLED) {
      throw notFound()
    }

    const { queryClient } = context

    // Catering visibility is a runtime toggle (Admin → Settings → Store).
    // Catering products all use the `catering-` handle prefix, so 404 their
    // detail pages too — otherwise they stay directly reachable and orderable
    // while catering is hidden.
    const { siteSettingsQueryOptions, isCateringVisible } = await import(
      "@/lib/data/site-settings"
    )
    const siteSettings = await queryClient.ensureQueryData(
      siteSettingsQueryOptions
    )
    if (
      !isCateringVisible(siteSettings) &&
      isStoreCatalogExcludedHandle(handle)
    ) {
      throw notFound()
    }
    const [{ getRegion }, { listProducts, retrieveProduct }] = await Promise.all([
      import("@/lib/data/regions"),
      import("@/lib/data/products"),
    ])

    const region = await queryClient.ensureQueryData({
      queryKey: ["region", countryCode],
      queryFn: () => getRegion({ country_code: countryCode }),
    })

    if (!region || !handle || isExpiredCampaignProductHandle(handle)) {
      throw notFound()
    }

    // Single comprehensive product fetch with all needed fields
    const product = await queryClient.ensureQueryData({
      queryKey: ["product", handle, region.id],
      queryFn: async () => {
        try {
          return await retrieveProduct({
            handle,
            region_id: region.id,
            fields:
              "*variants, +variants.inventory_quantity, +variants.manage_inventory, +variants.allow_backorder, +variants.calculated_price, *images, *options, *options.values, *collection, *tags",
          })
        } catch {
          throw notFound()
        }
      },
    })

    // Ensure related products are loaded for SSR to prevent hydration mismatch
    // This ensures consistent rendering between server and client
    await queryClient.ensureQueryData({
      queryKey: queryKeys.products.related(product.id, region.id),
      queryFn: async () => {
        const params: HttpTypes.StoreProductListParams = {
          fields: "title, handle, *thumbnail, *variants",
          is_giftcard: false,
          limit: 4,
        }

        if (product.collection_id) {
          params.collection_id = [product.collection_id]
        }

        if (product.tags && product.tags.length > 0) {
          params.tag_id = product.tags.map((tag) => tag.id)
        }

        const { products } = await listProducts({
          query_params: params,
          region_id: region.id,
        })

        return filterVisibleProducts(products).filter((p) => p.id !== product.id)
      },
    })

    return sanitize({
      countryCode,
      region,
      product: product as HttpTypes.StoreProduct,
    })
  },
  head: ({ loaderData }) => {
    const { product, region } = loaderData || {}
    if (!product) {
      return {
        meta: [
          {
            title: `Product Not Found | ${SITE_NAME}`,
          },
          { name: "robots", content: "noindex" },
        ],
      }
    }

    const canonical = `${SITE_URL}/products/${product.handle}`
    // Always emit a real OG/Twitter image — an empty content="" produces broken
    // social cards. Fall back to the brand 1200x630 card when a product has no
    // image or thumbnail.
    const ogImage =
      product.images?.[0]?.url ||
      product.thumbnail ||
      `${SITE_URL}/images/plusbake/optimized/og-home-1200x630.jpg`
    const descriptionContent =
      product.description ||
      `${product.title} — freshly baked at +Bake Cafe in Rancho Cucamonga, CA. Pre-order online for pickup.`
    const schemaPrice = product.variants?.[0]?.calculated_price?.calculated_amount

    // Reflect real inventory in structured data — advertising sold-out items as
    // InStock risks Google Merchant/rich-result penalties and erodes trust on a
    // bakery with limited-run items. A product is available if any variant is.
    const productInStock = (product.variants ?? []).some(isVariantInStock)
    const availability = productInStock
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock"

    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.title,
      description: descriptionContent,
      sku: product.variants?.[0]?.sku || product.id,
      image: product.images?.map((img: { url?: string }) => img.url).filter(Boolean) || [ogImage].filter(Boolean),
      url: canonical,
      brand: {
        "@type": "Brand",
        name: "+Bake Cafe",
      },
      offers: {
        "@type": "Offer",
        availability,
        priceCurrency: region?.currency_code?.toUpperCase(),
        price: typeof schemaPrice === "number"
          ? schemaPrice.toFixed(2)
          : undefined,
        url: canonical,
        priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        seller: {
          "@type": "Organization",
          name: "+Bake Cafe",
        },
      },
    }

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
        { "@type": "ListItem", position: 2, name: "Menu", item: `${SITE_URL}/menu` },
        { "@type": "ListItem", position: 3, name: product.title, item: canonical },
      ],
    }

    const firstImageUrl = product.images?.[0]?.url || product.thumbnail

    return {
      meta: [
        { title: `${product.title} | ${SITE_NAME} — Rancho Cucamonga` },
        { name: "description", content: descriptionContent.length > 160 ? `${descriptionContent.slice(0, 157)}...` : descriptionContent },
        { property: "og:title", content: `${product.title} | ${SITE_NAME}` },
        { property: "og:description", content: descriptionContent },
        { property: "og:type", content: "product" },
        { property: "og:url", content: canonical },
        { property: "og:image", content: ogImage },
        { property: "og:site_name", content: SITE_NAME },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${product.title} | ${SITE_NAME}` },
        { name: "twitter:description", content: descriptionContent },
        { name: "twitter:image", content: ogImage },
      ],
      links: [
        { rel: "canonical", href: canonical },
        ...(firstImageUrl
          ? [
              {
                rel: "preload",
                href: firstImageUrl,
                as: "image",
                fetchPriority: "high" as const,
              },
            ]
          : []),
      ],
      scripts: [
        { type: "application/ld+json", children: jsonLd(productSchema) },
        { type: "application/ld+json", children: jsonLd(breadcrumbSchema) },
      ],
    }
  },
  component: ProductDetails,
})
